import { setContext } from '@apollo/client/link/context/index';
import { InMemoryCache } from '@apollo/client/cache/inmemory/inMemoryCache';
import { ApolloClient, fromPromise } from '@apollo/client/core';
import ApolloService from 'ember-apollo-client/services/apollo';

import { service } from '@ember/service';
import { cancel, later } from '@ember/runloop';

import { appVersionString } from 'my-phorest/utils/app-version';
import {
  mergeCalendarDaysPolicy,
  mergeFeedConnectionPolicy,
} from 'my-phorest/utils/graphql';
import config from 'my-phorest/config/environment';

import { ApolloLink, Observable, split } from '@apollo/client/core';
import { RetryLink } from '@apollo/client/link/retry';
import { print } from 'graphql';
import { createClient } from 'graphql-ws';
import { getMainDefinition } from '@apollo/client/utilities';
import * as calendarCache from 'my-phorest/utils/calendar-cache';
import * as Sentry from '@sentry/browser';
import { onError } from '@apollo/client/link/error';

// WebSockets connection is pinged every 30s. We give server 5s to respond with pong.
// If server is not responding the WebSockets connection will be terminated and set up again.
const WS_KEEP_ALIVE_MS = 30_000; // 30s
const WS_PING_PONG_DISCONNECTION_MS = 5_000; // 5s

class QueryCache {
  constructor(cache) {
    this.cache = cache;
  }

  async insertInto(target, item) {
    const serialize = ({ __typename, id }) => {
      return JSON.stringify({ __typename, id });
    };
    let draft = await this._getDraft();
    let items = target(draft);
    let match = items.map(serialize).includes(serialize(item));
    if (!match) {
      items.push(item);
    }
    return this._saveDraft(draft);
  }

  async move({ from, to }, item) {
    let draft = await this._getDraft();
    let _from = from(draft);
    let toRemove = _from.findIndex((i) => i.id === item.id);
    _from.splice(toRemove, 1);

    to(draft).push(item);
    return this._saveDraft(draft);
  }

  async _getDraft() {
    let data = await this.cache.getData();
    return JSON.parse(JSON.stringify(data));
  }

  async _saveDraft(draft) {
    return this.cache.saveData(draft);
  }
}

export const queryCache = function (cache) {
  return new QueryCache(cache);
};

export default class OverriddenApolloService extends ApolloService {
  @service electronApp;
  @service errorHandler;
  @service fullCalendar;
  @service intl;
  @service notifications;
  @service session;
  @service swingBridge;
  @service versionTracker;

  _tokenRefreshPendingRequests = [];

  reinitClient() {
    this.client = new ApolloClient(this.clientOptions());
  }

  get options() {
    let { path, ...rest } = super.options;

    let host = this.session.isAuthenticated
      ? this.apiGatewayHost
      : 'https://dev.null'; // At present, only authenticated users can use GraphQL API

    return {
      ...rest,
      apiURL: `${host}/${path}`,
    };
  }

  clientOptions() {
    let clientOptions = super.clientOptions();

    return {
      ...clientOptions,
      connectToDevTools: config.apollo.connectToDevTools,
      name: 'my-phorest',
    };
  }

  cache() {
    return new InMemoryCache({
      possibleTypes: {
        RecurrenceUnion: [
          'MonthlyDateRecurrence',
          'MonthlyWeekDayRecurrence',
          'WeeklyRecurrence',
        ],
        BasketItemUnion: [
          'AppointmentBasketItem',
          'AppointmentCourseSessionBasketItem',
          'AppointmentPackageBasketItem',
          'AppointmentServiceRewardBasketItem',
          'AppointmentSpecialOfferBasketItem',
          'ExtraAppointmentCostBasketItem',
          'CancellationChargeBasketItem',
          'CourseBasketItem',
          'CourseSessionBasketItem',
          'CreditAccountAppointmentDepositBasketItem',
          'CreditAccountBasketItem',
          'MembershipBasketItem',
          'NoShowChargeBasketItem',
          'OfflineDepositBasketItem',
          'OnlineDepositBasketItem',
          'OpenSaleBasketItem',
          'PackageBasketItem',
          'ProductBasketItem',
          'ProductRewardBasketItem',
          'SaleFeeBasketItem',
          'ServiceBasketItem',
          'ServiceRewardBasketItem',
          'SpecialOfferBasketItem',
          'VoucherBasketItem',
          'VoucherTopUpBasketItem',
        ],
        BasketPaymentUnion: [
          'BasketIntegratedPayment',
          'BasketNonIntegratedPayment',
        ],
        CalendarEvent: ['Appointment', 'Break'],
        StaffCalendarEvent: ['Appointment', 'Break'],
      },
      typePolicies: {
        Query: {
          fields: {
            categoryServicesTree: {
              keyArgs: ['branchId'],
            },
            chatMessages: {
              keyArgs: ['clientId', 'channel'],
              merge: mergeFeedConnectionPolicy,
            },
            chainServiceCategories: {
              keyArgs: (args, context) => {
                let keyArgs = ['filterBy'];

                if (context.variables._paginated) {
                  keyArgs = [...keyArgs, 'after', 'first'];
                }
                return keyArgs;
              },
              merge: mergeFeedConnectionPolicy,
            },
            serviceCategories: {
              keyArgs: (args, context) => {
                let keyArgs = ['branchId', 'filterBy'];

                if (context.variables._paginated) {
                  keyArgs = [...keyArgs, 'after', 'first'];
                }
                return keyArgs;
              },
              merge: mergeFeedConnectionPolicy,
            },
            clientCategories: {
              keyArgs: ['filterBy'],
              merge: mergeFeedConnectionPolicy,
            },
            clients: {
              keyArgs: (args, context) => {
                let keyArgs = ['filterBy'];

                if (context.variables._paginated) {
                  keyArgs = [...keyArgs, 'after', 'first'];
                }
                return keyArgs;
              },
              merge: mergeFeedConnectionPolicy,
            },
            clientSources: {
              keyArgs: ['filterBy'],
              merge: mergeFeedConnectionPolicy,
            },
            clientMemberships: {
              keyArgs: (args, context) => {
                let keyArgs = ['filterBy'];

                if (context.variables._paginated) {
                  keyArgs = [...keyArgs, 'after', 'first'];
                }
                return keyArgs;
              },
              merge: mergeFeedConnectionPolicy,
            },
            clientRecurringServiceSubscriptions: {
              keyArgs: ['branchId', 'clientId', 'benefitDate'],
              merge: mergeFeedConnectionPolicy,
            },
            consultationFormSchemas: {
              keyArgs: ['filterBy'],
              merge: mergeFeedConnectionPolicy,
            },
            departments: {
              keyArgs: ['filterBy'],
              merge: mergeFeedConnectionPolicy,
            },
            departmentAssignments: {
              keyArgs: ['branchId'],
              merge: mergeFeedConnectionPolicy,
            },
            productBrandDepartmentAssignments: {
              keyArgs: ['branchId', 'brandId'],
              merge: mergeFeedConnectionPolicy,
            },
            serviceCategoryDepartmentAssignments: {
              keyArgs: ['branchId', 'categoryId'],
              merge: mergeFeedConnectionPolicy,
            },
            orders: {
              keyArgs: (args, context) => {
                let keyArgs = ['filterBy', 'branchId'];

                if (context.variables._paginated) {
                  keyArgs = [...keyArgs, 'after', 'first'];
                }
                return keyArgs;
              },
              merge: mergeFeedConnectionPolicy,
            },
            products: {
              keyArgs: (args, context) => {
                let keyArgs = ['filterBy', 'branchId'];

                if (context.variables._paginated) {
                  keyArgs = [...keyArgs, 'after', 'first'];
                }
                return keyArgs;
              },
              merge: mergeFeedConnectionPolicy,
            },
            brandsConnection: {
              keyArgs: (args, context) => {
                let keyArgs = ['branchId', 'filterBy'];
                if (context.variables?._paginated) {
                  keyArgs = [...keyArgs, 'after', 'first'];
                }
                return keyArgs;
              },
              merge: mergeFeedConnectionPolicy,
            },
            chainBrandsConnection: {
              keyArgs: ['filterBy'],
              merge: mergeFeedConnectionPolicy,
            },
            globalBrands: {
              keyArgs: (args, context) => {
                let keyArgs = ['filterBy'];
                if (context.variables?._paginated) {
                  keyArgs = [...keyArgs, 'after', 'first'];
                }
                return keyArgs;
              },
              merge: mergeFeedConnectionPolicy,
            },
            globalBrandsConnection: {
              keyArgs: (args, context) => {
                let keyArgs = ['filterBy'];
                if (context.variables?._paginated) {
                  keyArgs = [...keyArgs, 'after', 'first'];
                }
                return keyArgs;
              },
              merge: mergeFeedConnectionPolicy,
            },
            accountingSyncResultsConnection: {
              keyArgs: (args, context) => {
                let keyArgs = ['branchId', 'accountingPlatform', 'filterBy'];
                if (context.variables?._paginated) {
                  keyArgs = [...keyArgs, 'after', 'first'];
                }
                return keyArgs;
              },
              merge: mergeFeedConnectionPolicy,
            },
            productCategories: {
              keyArgs: ['branchId', 'filterBy'],
              merge: mergeFeedConnectionPolicy,
            },
            chainProductCategories: {
              keyArgs: ['filterBy'],
              merge: mergeFeedConnectionPolicy,
            },
            saleFees: {
              keyArgs: ['branchId'],
              merge: mergeFeedConnectionPolicy,
            },
            staffConnection: {
              keyArgs: ['filterBy', 'branchId'],
              merge: mergeFeedConnectionPolicy,
            },
            clientPhotos: {
              keyArgs: ['clientId', 'date'],
              merge: mergeFeedConnectionPolicy,
            },
            appointmentNotes: {
              keyArgs: ['filterBy', 'branchId'],
              merge: mergeFeedConnectionPolicy,
            },
            productRewards: {
              keyArgs: (args, context) => {
                let keyArgs = ['filterBy', 'branchId'];
                if (context.variables?._paginated) {
                  keyArgs = [...keyArgs, 'after', 'first'];
                }
                return keyArgs;
              },
              merge: mergeFeedConnectionPolicy,
            },
            serviceRewards: {
              keyArgs: (args, context) => {
                let keyArgs = ['filterBy', 'branchId'];
                if (context.variables?._paginated) {
                  keyArgs = [...keyArgs, 'after', 'first'];
                }
                return keyArgs;
              },
              merge: mergeFeedConnectionPolicy,
            },
            serviceHistory: {
              keyArgs: (args, context) => {
                let keyArgs = ['clientId', 'filterBy'];

                if (context.variables._paginated) {
                  keyArgs = [...keyArgs, 'after', 'first'];
                }
                return keyArgs;
              },
              merge: mergeFeedConnectionPolicy,
            },
            services: {
              keyArgs: [
                'filterBy',
                'servicePriceContext',
                'serviceDurationContext',
                'branchId',
              ],
              merge: mergeFeedConnectionPolicy,
            },
            stockTakeList: {
              keyArgs: (args, context) => {
                let keyArgs = ['filterBy', 'branchId'];

                if (context.variables._paginated) {
                  keyArgs = [...keyArgs, 'after', 'first'];
                }
                return keyArgs;
              },
              merge: mergeFeedConnectionPolicy,
            },
            taxRates: {
              keyArgs: (args, context) => {
                let keyArgs = ['filterBy', 'branchId'];
                if (context.variables?._paginated) {
                  keyArgs = [...keyArgs, 'after', 'first'];
                }
                return keyArgs;
              },
              merge: mergeFeedConnectionPolicy,
            },
            tills: {
              keyArgs: (args, context) => {
                let keyArgs = ['branchId'];
                if (context.variables?._paginated) {
                  keyArgs = [...keyArgs, 'after', 'first'];
                }
                return keyArgs;
              },
              merge: mergeFeedConnectionPolicy,
            },
            vouchersByClientId: {
              keyArgs: (args, context) => {
                let keyArgs = ['clientId'];

                if (context.variables._paginated) {
                  keyArgs = [...keyArgs, 'after', 'first'];
                }
                return keyArgs;
              },
              merge: mergeFeedConnectionPolicy,
            },
            voucherCampaigns: {
              keyArgs: (args, context) => {
                let keyArgs = ['branchId', 'filterBy'];

                if (context.variables._paginated) {
                  keyArgs = [...keyArgs, 'after', 'first'];
                }
                return keyArgs;
              },
              merge: mergeFeedConnectionPolicy,
            },
            waitlistBookings: {
              keyArgs: ['branchId', 'filterBy'],
              merge: mergeFeedConnectionPolicy,
            },
            memberships: {
              keyArgs: (args, context) => {
                let keyArgs = ['branchId', 'filterBy'];

                if (context.variables._paginated) {
                  keyArgs = [...keyArgs, 'after', 'first'];
                }
                return keyArgs;
              },
              merge: mergeFeedConnectionPolicy,
            },
            purchasedProducts: {
              keyArgs: (args, context) => {
                let keyArgs = ['clientId', 'filterBy'];

                if (context.variables._paginated) {
                  keyArgs = [...keyArgs, 'after', 'first'];
                }
                return keyArgs;
              },
              merge: mergeFeedConnectionPolicy,
            },
            suppliers: {
              keyArgs: (args, context) => {
                let keyArgs = ['branchId', 'filterBy'];
                if (context.variables?._paginated) {
                  keyArgs = [...keyArgs, 'after', 'first'];
                }
                return keyArgs;
              },
              merge: mergeFeedConnectionPolicy,
            },
          },
        },
        Basket: {
          fields: {
            items: {
              keyArgs: false,
              merge: false,
            },
          },
        },
        Branch: {
          fields: {
            payment: {
              merge: true,
            },
          },
        },
        Client: {
          fields: {
            treatCard: {
              merge: false,
            },
          },
        },
        LibraryFilterOperator: {
          keyFields: ['id', 'selection', ['xmlId']],
        },
        LegacyReportFilterBaseSpec: {
          keyFields: false,
        },
        LegacyReportFilterIdentitySpec: {
          keyFields: false,
        },
        PrintRequestSubscriptionResource: {
          keyFields: ['printId'],
        },
        ServiceGroup: {
          fields: {
            // `color` is a [local-only field](https://www.apollographql.com/docs/react/local-state/managing-state-with-field-policies/).
            color: {
              read() {
                // Packages don't have colors but are displayed in the same way as other items on the lists - with an avatar.
                // This value sets the same (background) color to all packages.
                return {
                  hex: '#F3F4F6',
                };
              },
            },
          },
        },
        Staff: {
          fields: {
            calendarDays: {
              keyArgs: [],
              merge: mergeCalendarDaysPolicy,
            },
          },
        },
        Machine: {
          fields: {
            calendarDays: {
              keyArgs: [],
              merge: mergeCalendarDaysPolicy,
            },
          },
        },
        Room: {
          fields: {
            calendarDays: {
              keyArgs: [],
              merge: mergeCalendarDaysPolicy,
            },
          },
        },
        WaitlistBooking: {
          fields: {
            items: {
              keyArgs: false,
              merge: false,
            },
            availabilityPreferences: {
              keyArgs: false,
              merge: false,
            },
          },
        },
        MembershipRecurringServiceBenefit: {
          fields: {
            applicableServices: {
              merge: false,
            },
          },
        },
        ClientMembership: {
          fields: {
            chainMembership: {
              merge: false,
            },
          },
        },
        // These types can be returned with null id, so use branch.id as a fallback
        CourseBranchOverride: {
          keyFields: ['id', 'branch', ['id']],
        },
        ServiceGroupBranchOverride: {
          keyFields: ['id', 'branch', ['id']],
          fields: {
            excludedStaffMembers: {
              keyArgs: false,
              merge: false,
            },
            includedStaffMembers: {
              keyArgs: false,
              merge: false,
            },
          },
        },
        ChainServiceGroup: {
          fields: {
            items: {
              keyArgs: false,
              merge: false,
            },
          },
        },
        ProductBranchOverride: {
          keyFields: ['id', 'branch', ['id']],
        },
        ProductSpecialOfferBranchOverride: {
          keyFields: ['id', 'branch', ['id']],
        },
        StockTakeItem: {
          keyFields: ['id', 'product', ['id']],
        },
        StockTransferItem: {
          keyFields: ['id', 'fromProduct', ['id']],
        },
      },
    });
  }

  createErrorLink() {
    return onError(({ networkError, operation, forward }) => {
      if (networkError?.statusCode === 401) {
        if (!this.session.renewAccessTokenTask.isRunning) {
          return fromPromise(
            this.session.renewAccessTokenTask.perform().then(() => {
              operation.setContext(({ headers = {} }) => ({
                headers: {
                  ...headers,
                  authorization: `Bearer ${this.session.accessToken}`,
                },
              }));
            })
          ).flatMap(() => {
            this._tokenRefreshPendingRequests.map((callback) => callback());
            this._tokenRefreshPendingRequests = [];

            return forward(operation);
          });
        } else {
          return fromPromise(
            new Promise((resolve) => {
              this._tokenRefreshPendingRequests.push(() => {
                operation.setContext(({ headers = {} }) => ({
                  headers: {
                    ...headers,
                    authorization: `Bearer ${this.session.accessToken}`,
                  },
                }));
                return resolve();
              });
            })
          ).flatMap(() => {
            return forward(operation);
          });
        }
      }
    });
  }

  createWsLink(webSocketsUrl) {
    let activeSocket, timedOut;
    let disconnected = false;

    return new WebSocketLink({
      url: webSocketsUrl,
      connectionParams: () => {
        const { accessToken, securityScope } = this.session;
        return {
          authorization: `Bearer ${accessToken}`,
          'x-security-business-scope': securityScope,
        };
      },
      keepAlive: WS_KEEP_ALIVE_MS,
      retryAttempts: 5,
      shouldRetry: (/* errOrCloseEvent */) => {
        return true;
      },
      on: {
        closed: () => {
          disconnected = true;
        },
        connected: (socket) => {
          activeSocket = socket;

          // If WS disconnection happened, and we are connected again, clear all calendar cache and refetch current calendar.
          if (disconnected) {
            Sentry.addBreadcrumb({
              category: 'network',
              message: 'WebSockets reconnected after ping-pong failure.',
              level: 'info',
            });
            calendarCache.invalidate(this.client.cache);
            this.fullCalendar.refetchResourcesSilently();
          }
          disconnected = false;
        },
        error: (error) => {
          Sentry.addBreadcrumb({
            category: 'network',
            message: `WebSockets connection error code: ${error?.code}`,
            level: 'error',
          });
        },
        ping: (received) => {
          if (!received)
            timedOut = later(() => {
              if (activeSocket?.readyState === WebSocket.OPEN) {
                Sentry.addBreadcrumb({
                  category: 'network',
                  message:
                    "Didn't receive pong in 5s from WebSockets server. Closing active socket.",
                  level: 'info',
                });

                activeSocket.close(4408, 'Request Timeout (no "pong")');
              }
            }, WS_PING_PONG_DISCONNECTION_MS);
        },
        pong: (received) => {
          if (received) cancel(timedOut);
        },
      },
    });
  }

  link() {
    const httpLink = super.link();

    let authMiddleware = setContext(async (_request, context) => {
      let token = this.session.accessToken;
      let securityScope = this.session.securityScope;
      let appVersion = appVersionString(
        config.APP,
        this.swingBridge,
        this.electronApp
      );

      context.headers = {
        ...context.headers,
        authorization: `Bearer ${token}`,
        'x-memento-security-context': securityScope, // TODO: rename header key to: 'x-security-business-scope'
        'x-app-version': appVersion,
        'x-phorest-application-id': 'my-phorest',
        'x-phorest-application-instance-id':
          this.versionTracker.applicationInstanceId,
      };

      return context;
    });

    const retryLink = new RetryLink({
      attempts: (count, operation, error) => {
        const isErrorWorthRetrying =
          !!error && (error.statusCode > 500 || error.networkError);

        if (isErrorWorthRetrying) {
          this.errorHandler.handle(error, {
            showError: false,
            sentryLevel: 'warning',
            sentryTags: {
              requestRetryAttempt: count,
              requestRetryOperation: operation.operationName,
            },
          });
        }

        return count <= 3 && isErrorWorthRetrying;
      },
      delay: (count) => {
        return Math.min(count * 1000, 5 * 1000);
      },
    });

    const errorLink = this.createErrorLink();

    if (!this.session || !this.session.silo) {
      return authMiddleware.concat(httpLink);
    } else {
      let wsLink = this.createWsLink(this.webSocketsUrl);

      return split(
        ({ query }) => {
          let { kind, operation } = getMainDefinition(query);

          return kind === 'OperationDefinition' && operation === 'subscription';
        },
        wsLink,
        authMiddleware.concat(retryLink).concat(errorLink).concat(httpLink)
      );
    }
  }

  get apiGatewayHost() {
    const { host } = config.apollo;
    if (host) {
      return host;
    }
    const { apiGatewayEU, apiGatewayUS } = config.APP;
    const { silo } = this.session;

    if (!silo) {
      throw new Error('Cannot choose API Gateway without a silo!');
    }

    return {
      eu: apiGatewayEU,
      us: apiGatewayUS,
    }[silo.toLowerCase()];
  }

  get webSocketsUrl() {
    const { hostEU, hostUS, path } = config.apollo.webSockets;
    const { silo } = this.session;

    if (!silo) {
      throw new Error('Cannot choose WebSockets URL without a silo!');
    }

    return {
      eu: `${hostEU}/${path}`,
      us: `${hostUS}/${path}`,
    }[silo.toLowerCase()];
  }
}

class WebSocketLink extends ApolloLink {
  client;

  constructor(options) {
    super();
    this.client = createClient(options);
  }

  request(operation) {
    return new Observable((sink) => {
      return this.client.subscribe(
        { ...operation, query: print(operation.query) },
        {
          next: sink.next.bind(sink),
          complete: sink.complete.bind(sink),
          error: sink.error.bind(sink),
        }
      );
    });
  }
}
